import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import ModalLabel from '../../../components/modal-label/modal-label.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import ModalFieldBlock from '../../../components/modal-field-block/modal-field-block.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import FieldSelect from '../../../components/field-select/field-select.component';
import FieldText from '../../../components/field-text/field-text.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import { DEAL_STATUS_DEAD_DEAL } from '../../../constants/entity/deal-statuses';
import { FILTER_VALUE_EMPTY } from '../../../constants/system/filter-values';

const DealRejectModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ITEM_ID_OTHER: number = -1;

  const {
    isOpen,
    dealRejectReasonList,
    onConfirm,
    onCloseModal,
  } = props;

  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [selectedDealRejectReason, setSelectedDealRejectReason]: [string | null, Function] = useState(null);
  const [isDropdownDisabled, setIsDropdownDisabled]: [boolean | null, Function] = useState(false);
  const [isTextFieldDisabled, setIsTextFieldDisabled]: [boolean | null, Function] = useState(false);
  const [dealRejectReasonTitle, setDealRejectReasonTitle]: [string | null, Function] = useState(null);
  const [dealRejectReasonComment, setDealRejectReasonComment]: [string | null, Function] = useState(null);

  const commonDealRejectReasonList: DealRejectReason[] = useMemo((): DealRejectReason[] => [
    ...(dealRejectReasonList || []),
    {
      id: ITEM_ID_OTHER,
      name: 'other',
      title: 'Other',
    },
  ], [dealRejectReasonList]);

  const menuItemDealRejectReasonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: commonDealRejectReasonList,
  }), [commonDealRejectReasonList]);

  const confirm: MouseEventHandlerFunction = useCallback((): void => {
    if (!dealRejectReasonTitle) {
      setErrorMessage('Reason is required');
    }

    if (dealRejectReasonTitle) {
      setErrorMessage(null);

      if (onConfirm) {
        onConfirm(DEAL_STATUS_DEAD_DEAL, dealRejectReasonTitle, dealRejectReasonComment || null);
      }
    }
  }, [dealRejectReasonTitle, dealRejectReasonComment]);

  const onDealRejectReasonDropdownChange: Function = useCallback((dealRejectReason: string): void => {
    setErrorMessage(null);
    setSelectedDealRejectReason(dealRejectReason);

    const selectedDealRejectReason: DealRejectReason = dealRejectReasonList && dealRejectReasonList.length
      ? dealRejectReasonList.find((dealRejectReasonItem: DealRejectReason): boolean => (
        dealRejectReasonItem.name === dealRejectReason
      )) : null;

    if (selectedDealRejectReason) {
      setDealRejectReasonTitle(selectedDealRejectReason.title);
      setIsTextFieldDisabled(true);
    } else {
      setIsTextFieldDisabled(false);
    }
  }, []);

  const onDealRejectReasonChange: Function = useCallback((dealRejectReasonValue: string): void => {
    setErrorMessage(null);
    setSelectedDealRejectReason(null);
    setDealRejectReasonTitle(dealRejectReasonValue);
    setIsDropdownDisabled(!!dealRejectReasonValue);
  }, []);

  const onDealRejectReasonCommentChange: Function = useCallback((dealRejectReasonCommentValue: string): void => {
    setDealRejectReasonComment(dealRejectReasonCommentValue);
  }, []);

  const closeModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  return (
    <ModalLayout isOpen={isOpen} title="Dead Deal Reason" onCloseModal={closeModal}>
      <Card isContainer>
        <ModalLabel>
          Please select a reason:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldSelect
            isPlain
            isBordered
            withBlankElement
            isDisabled={isDropdownDisabled}
            value={selectedDealRejectReason}
            blankElementValue={FILTER_VALUE_EMPTY}
            onChange={(event: EventSelectChange, value: string): void => {
              onDealRejectReasonDropdownChange(value);
            }}
          >
            {menuItemDealRejectReasonList}
          </FieldSelect>
        </ModalFieldBlock>
        <ModalLabel>
          Or if there is no appropriate reason in the list, you can specify custom one:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldText
            isPlain
            isBordered
            isDisabled={isTextFieldDisabled}
            maxLength={FormatService.STRING_LIMIT_DEFAULT}
            onChange={(event: React.ChangeEvent, value: string): void => {
              onDealRejectReasonChange(value);
            }}
          />
        </ModalFieldBlock>
        <ModalLabel>
          Add comment:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldText
            isPlain
            isBordered
            isMultiline
            maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
            minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
            onChange={(event: React.ChangeEvent, value: string): void => {
              onDealRejectReasonCommentChange(value);
            }}
          />
        </ModalFieldBlock>
      </Card>
      <ModalButtonBlock>
        <Button onClick={closeModal}>
          Cancel
        </Button>
        <Button isActive onClick={confirm}>
          Confirm
        </Button>
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
    </ModalLayout>
  );
});

export default DealRejectModal;
